/**
 * Form primitives.
 *
 * TECH_STACK.md §3 names shadcn/ui as the toolkit. These are deliberately shadcn-shaped — the
 * same props, the same class composition through `cn` — but hand-written, because a text input
 * and a button gain nothing from a Radix primitive. The components that do gain from one
 * (Select, Dialog, Popover) arrive with the review screen in Milestone 4, which is the right
 * moment to run `shadcn add` and let these be replaced.
 *
 * Touch targets are at least 44px tall, which is the minimum that is comfortable on a phone.
 */

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

export function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-stone-700">
      {children}
    </label>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ className, invalid, ...props }: InputProps) {
  return (
    <input
      {...props}
      aria-invalid={invalid || undefined}
      className={cn(
        "min-h-11 w-full rounded-lg border bg-white px-3 py-2 text-base",
        "placeholder:text-stone-400",
        "focus:ring-brand-600 focus:border-brand-600 focus:ring-1 focus:outline-none",
        invalid ? "border-red-500" : "border-stone-300",
        className,
      )}
    />
  );
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-sm text-red-700">
      {children}
    </p>
  );
}

/** A field's label, control and message as one block, so pages do not repeat the markup. */
export function Field({
  name,
  label,
  error,
  children,
}: {
  name: string;
  label: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name}>{label}</Label>
      {children}
      <FieldError>{error}</FieldError>
    </div>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
}

export function Button({ className, variant = "primary", ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        "min-h-11 w-full rounded-lg px-4 py-2 text-base font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-60",
        variant === "primary"
          ? "bg-brand-700 hover:bg-brand-800 text-white"
          : "border border-stone-300 bg-white text-stone-800 hover:bg-stone-50",
        className,
      )}
    />
  );
}

/** A message about the whole form or page, not one field. */
export function Notice({
  tone,
  children,
}: {
  tone: "error" | "success" | "info";
  children: ReactNode;
}) {
  const tones = {
    error: "bg-red-50 text-red-800",
    success: "bg-brand-50 text-brand-800",
    info: "bg-stone-100 text-stone-700",
  } as const;
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={cn("rounded-lg p-3 text-sm", tones[tone])}
    >
      {children}
    </p>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/** A native select: on a phone the platform picker beats anything we could build. */
export function Select({ className, invalid, ...props }: SelectProps) {
  return (
    <select
      {...props}
      aria-invalid={invalid || undefined}
      className={cn(
        "min-h-11 w-full rounded-lg border bg-white px-3 py-2 text-base",
        "focus:ring-brand-600 focus:border-brand-600 focus:ring-1 focus:outline-none",
        invalid ? "border-red-500" : "border-stone-300",
        className,
      )}
    />
  );
}
